/**
 * Compile-time canned-response table for {@link StubChatbotTransport}.
 *
 * Encodes the exact table from
 * `.kiro/specs/help-docs-panel/design.md` § Example Usage.
 * The stub transport matches an incoming `input.message` case-insensitively
 * against each row's `keywords` and returns the row's `{ text, citedDocs }`
 * when a keyword is found as a substring of the message.
 *
 * This module is a **leaf constant** — it deliberately has no dependencies
 * (not even on `./types.ts`) so it can be consumed by both the transport
 * implementation (Wave 2 task 2.8) and future property tests without pulling
 * the view-type surface.
 *
 * @see .kiro/specs/help-docs-panel/design.md — § Example Usage (STUB_CANNED_TABLE)
 * @see .kiro/specs/help-docs-panel/requirements.md — Requirement 9.3
 */

/**
 * Shape of a single row in {@link STUB_CANNED_TABLE}.
 *
 * @remarks
 * - `keywords` — non-empty list of lowercase substrings matched against the
 *   user's message (case-insensitively) by `StubChatbotTransport.send`.
 * - `text` — the canned response body, rendered verbatim in the dock.
 * - `citedDocs` — manifest-relative doc paths (no leading `/`, no `..`,
 *   every entry ends in `.md`). Each becomes a `CitationChip`.
 */
export interface StubCannedRow {
  readonly keywords: readonly string[];
  readonly text: string;
  readonly citedDocs: readonly string[];
}

/**
 * The 4-row canned-response table from design § Example Usage.
 *
 * Frozen at runtime (defense in depth) in addition to being `readonly` at
 * compile time — a caller who casts through `any` and attempts to mutate
 * will trigger `TypeError` in strict mode.
 *
 * Row-by-row summary (matched keywords):
 * 1. `"statement"`, `"collection rate"`
 * 2. `"import"`, `"csv"`
 * 3. `"error"`, `"err-"`
 * 4. `"building"`
 *
 * @see .kiro/specs/help-docs-panel/design.md — § Example Usage (STUB_CANNED_TABLE)
 * @see .kiro/specs/help-docs-panel/requirements.md — Requirement 9.3
 */
export const STUB_CANNED_TABLE: readonly StubCannedRow[] = Object.freeze([
  Object.freeze({
    keywords: Object.freeze(["statement", "collection rate"] as const),
    text:
      "Statements are covered in api/statements.md. The collection rate is the ratio of paid vs demanded service charges — see the linked doc.",
    citedDocs: Object.freeze([
      "api/statements.md",
      "api/statements-dataflow.md",
    ] as const),
  }),
  Object.freeze({
    keywords: Object.freeze(["import", "csv"] as const),
    text:
      "The import flow is documented in features/import.md — start there for CSV format and reconciliation steps.",
    citedDocs: Object.freeze(["features/import.md"] as const),
  }),
  Object.freeze({
    keywords: Object.freeze(["error", "err-"] as const),
    text:
      "Errors follow the platform error handling pattern — see the guide and categories docs.",
    citedDocs: Object.freeze([
      "error-handling/guide.md",
      "error-handling/categories.md",
    ] as const),
  }),
  Object.freeze({
    keywords: Object.freeze(["building"] as const),
    text:
      "The Building model is defined in architecture/models.md — see the Building section.",
    citedDocs: Object.freeze(["architecture/models.md"] as const),
  }),
] as const);
